import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CalendarPlus,
  CheckSquare,
  Moon,
  NotebookPen,
  Plus,
  Search,
  Settings,
  Sun,
  Target,
  Sunrise,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { useLiveRecords, useReminders } from "@/hooks/useDomain";
import { useThemeStore } from "@/lib/theme";
import { cn } from "@/lib/cn";

interface Command {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  run: () => void;
}

/** Accent-insensitive search: "hop" matches "Họp". */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();
}

export function CommandPalette({
  open,
  onClose,
  onCreateEvent,
  onCreateTask,
}: {
  open: boolean;
  onClose: () => void;
  onCreateEvent: () => void;
  onCreateTask: () => void;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const events = useLiveRecords<{
    id: string;
    title: string;
    startEpochMillis: number;
  }>("events");
  const reminders = useReminders();
  const notes = useLiveRecords<{ id: string; title: string }>("stickyNotes");
  const habits = useLiveRecords<{ id: string; title: string }>("habits");
  const themeMode = useThemeStore((s) => s.mode);
  const setTheme = useThemeStore((s) => s.setMode);

  const close = () => {
    setQuery("");
    setActive(0);
    onClose();
  };

  const commands = useMemo<Command[]>(() => {
    const go = (path: string) => () => {
      navigate(path);
      close();
    };

    const list: Command[] = [
      { id: "nav-today", group: "Điều hướng", label: "Hôm nay", icon: Sunrise, run: go("/") },
      { id: "nav-calendar", group: "Điều hướng", label: "Lịch", icon: CalendarDays, run: go("/calendar") },
      { id: "nav-tasks", group: "Điều hướng", label: "Việc", icon: CheckSquare, run: go("/tasks") },
      { id: "nav-habits", group: "Điều hướng", label: "Thói quen", icon: Target, run: go("/habits") },
      { id: "nav-notes", group: "Điều hướng", label: "Ghi chú", icon: NotebookPen, run: go("/notes") },
      { id: "nav-settings", group: "Điều hướng", label: "Cài đặt", icon: Settings, run: go("/settings") },
      {
        id: "new-event",
        group: "Tạo mới",
        label: "Sự kiện mới",
        icon: CalendarPlus,
        run: () => {
          close();
          onCreateEvent();
        },
      },
      {
        id: "new-task",
        group: "Tạo mới",
        label: "Việc mới",
        icon: Plus,
        run: () => {
          close();
          onCreateTask();
        },
      },
      {
        id: "theme",
        group: "Giao diện",
        label: themeMode === "dark" ? "Chuyển sang giao diện sáng" : "Chuyển sang giao diện tối",
        icon: themeMode === "dark" ? Sun : Moon,
        run: () => {
          setTheme(themeMode === "dark" ? "light" : "dark");
          close();
        },
      },
    ];

    for (const event of [...events].sort((a, b) => a.startEpochMillis - b.startEpochMillis).slice(0, 60)) {
      list.push({
        id: `event-${event.id}`,
        group: "Sự kiện",
        label: event.title,
        hint: new Date(event.startEpochMillis).toLocaleDateString("vi-VN"),
        icon: CalendarDays,
        run: go("/calendar"),
      });
    }

    for (const reminder of reminders.slice(0, 60)) {
      list.push({
        id: `reminder-${reminder.id}`,
        group: "Việc",
        label: reminder.title,
        hint: reminder.isCompleted ? "đã xong" : undefined,
        icon: CheckSquare,
        run: go("/tasks"),
      });
    }

    for (const note of notes.slice(0, 40)) {
      list.push({
        id: `note-${note.id}`,
        group: "Ghi chú",
        label: note.title || "(không tiêu đề)",
        icon: NotebookPen,
        run: go(`/notes/${note.id}`),
      });
    }

    for (const habit of habits.slice(0, 40)) {
      list.push({
        id: `habit-${habit.id}`,
        group: "Thói quen",
        label: habit.title,
        icon: Target,
        run: go("/habits"),
      });
    }

    return list;
    // `close` is stable enough: it only closes the palette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, reminders, notes, habits, themeMode, navigate, onCreateEvent, onCreateTask, setTheme]);

  const filtered = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) {
      // Default view: navigation, creation and appearance only, keeps the list short.
      return commands.filter(
        (c) =>
          c.group === "Điều hướng" ||
          c.group === "Tạo mới" ||
          c.group === "Giao diện",
      );
    }
    return commands
      .filter(
        (c) =>
          normalize(c.label).includes(needle) ||
          normalize(c.group).includes(needle),
      )
      .slice(0, 24);
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      window.setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const groups: { name: string; items: Command[] }[] = [];
  for (const command of filtered) {
    const existing = groups.find((g) => g.name === command.group);
    if (existing) existing.items.push(command);
    else groups.push({ name: command.group, items: [command] });
  }

  // Flat order used by the arrow keys.
  const flat = groups.flatMap((group) => group.items);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index + 1) % Math.max(1, flat.length));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index - 1 + flat.length) % Math.max(1, flat.length));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      flat[active]?.run();
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[55] flex items-start justify-center px-4 pt-[12vh]">
      <div
        className="absolute inset-0 animate-fade-in bg-black/30 backdrop-blur-[2px] dark:bg-black/60"
        onClick={close}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Tìm kiếm và lệnh nhanh"
        onKeyDown={onKeyDown}
        className="relative w-full max-w-[560px] animate-scale-in overflow-hidden rounded-xl border border-line bg-paper shadow-pop"
      >
        <div className="flex items-center gap-2.5 border-b border-line px-4">
          <Search size={16} className="shrink-0 text-ink-faint" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm sự kiện, việc, ghi chú… hoặc nhập lệnh"
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
            role="combobox"
            aria-expanded
            aria-controls="command-results"
            aria-autocomplete="list"
          />
          <kbd className="hidden shrink-0 rounded border border-line px-1.5 py-0.5 font-sans text-[10px] text-ink-faint sm:block">
            Esc
          </kbd>
        </div>

        <div
          id="command-results"
          role="listbox"
          className="max-h-[52vh] overflow-y-auto p-1.5"
        >
          {flat.length === 0 ? (
            <p className="px-3 py-8 text-center text-[13px] text-ink-faint">
              Không tìm thấy “{query}”.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.name} className="mb-1">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                  {group.name}
                </p>
                {group.items.map((command) => {
                  const index = flat.indexOf(command);
                  const isActive = index === active;
                  return (
                    <button
                      key={command.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActive(index)}
                      onClick={command.run}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-[13px] transition",
                        isActive
                          ? "bg-shell text-ink"
                          : "text-ink-soft hover:bg-shell",
                      )}
                    >
                      <command.icon size={15} className="shrink-0 text-ink-faint" />
                      <span className="min-w-0 flex-1 truncate">{command.label}</span>
                      {command.hint && (
                        <span className="shrink-0 text-[11px] text-ink-faint">
                          {command.hint}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-line px-3 py-2 text-[11px] text-ink-faint">
          <span>↑↓ chọn</span>
          <span>↵ mở</span>
          <span className="ml-auto">⌘K</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
